import React, { useState } from "react";
import { MailWarning, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyBanner() {
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified || user.role === "admin") return null;

  const verify = async () => {
    if (code.trim().length < 4) { toast.error("Enter the 6-digit code from your email"); return; }
    setVerifying(true);
    try {
      await api.post("/auth/verify-email", { code: code.trim() });
      toast.success("Email verified — you're all set!");
      await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast.success(data.status === "already_verified" ? "Already verified!" : "A fresh code is on its way to your inbox");
      if (data.status === "already_verified") await refreshUser();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="verify-banner" className="relative z-30 border-b border-amber-400/30 bg-gradient-to-r from-[#FF8A1E]/15 via-[#FF2D78]/10 to-[#A020F0]/15">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <MailWarning className="w-5 h-5 text-amber-400 shrink-0" />
          <p className="text-sm text-slate-200">
            <span className="font-semibold text-white">Please verify your email</span> to unlock bookings & posting. We sent a 6-digit code to <span className="font-semibold text-amber-300">{user.email}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
          <input
            data-testid="verify-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="000000"
            className="w-24 text-center tracking-[0.3em] font-mono-caption bg-[#0B0E1B] border border-[#1D2640] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60"
          />
          <button data-testid="verify-code-btn" onClick={verify} disabled={verifying}
            className="btn-neon rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60">
            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verify
          </button>
          <button data-testid="verify-resend-btn" onClick={resend} disabled={sending}
            className="glass rounded-lg px-3 py-2 text-sm text-cyan-300 hover:text-white transition-colors disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resend Code"}
          </button>
        </div>
      </div>
    </div>
  );
}
